import { Avatar, Col, FlexboxGrid, Grid, Row } from 'rsuite'
import { useUserSubscriptions } from '../../../ws/useUserSubscriptions'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { userSelector } from '../../../features/user/userSlice'
import { useUsersSubscriptions } from '../../../ws/useUsersSubscriptions'
import React, { useEffect } from 'react'
import { CreateRoom, RoomType } from '../../../misc/domain/room'
import { postUserRoomCreate } from '../../../api/rest'
import { navigatePage } from '../../../misc/utils/navigation'
import { useNavigate } from 'react-router-dom'
import ExitIcon from '@rsuite/icons/Exit'
import { useAuthContext } from '../../../auth/AuthContext'
import BeforeCallUserMobile from '../../video/before-call-user/BeforeCallUserMobile'

// The page must fit any phone viewport without scrolling (WrapperPage is 100vh with
// overflow hidden), so every vertical size scales with the viewport via clamp()/vh
// instead of fixed pixels. Minimums keep it usable on short screens, maximums keep
// the roomy tablet look.

const welcomeMessageStyle: React.CSSProperties = {
  color: 'white',
  fontFamily: 'Alegreya Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: 'clamp(19px, 2.6vh, 25px)',
  lineHeight: 1.2
}

const welcomeMessageNameStyle: React.CSSProperties = {
  ...welcomeMessageStyle,
  fontWeight: 600
}

const callUsStyle: React.CSSProperties = {
  color: 'white',
  fontFamily: 'Alegreya Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: 'clamp(16px, 2.2vh, 20px)',
  lineHeight: 1.1,
  textAlign: 'center'
}

const statsContainerStyle: React.CSSProperties = {
  marginTop: 'clamp(6px, 1.2vh, 10px)',
  backgroundColor: '#28454D',
  borderRadius: 25,
  padding: 'clamp(6px, 1.2vh, 10px)',
}

const statsNumberStyle: React.CSSProperties = {
  fontFamily: 'Alegreya Sans',
  fontStyle: 'normal',
  fontWeight: 800,
  fontSize: 'clamp(34px, 5vh, 60px)',
  lineHeight: 1,
  color: '#B0DA7C',
  textAlign: 'center',
}

const statsImageStyle: React.CSSProperties = {
  marginTop: 'clamp(8px, 1.5vh, 20px)',
  padding: 'clamp(8px, 1.5vh, 15px)',
  borderRadius: 150,
  backgroundColor: '#233C42',
  textAlign: 'center',
  width: 'clamp(48px, 7.5vh, 80px)',
  height: 'clamp(48px, 7.5vh, 80px)'
}

const statsLabelStyle: React.CSSProperties = {
  marginTop: 'clamp(6px, 1vh, 10px)',
  fontFamily: 'Alegreya Sans',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: 'clamp(11px, 1.6vh, 14px)',
  lineHeight: 1.2,
  color: '#B0DA7C',
  textAlign: 'center'
}

const callButtonStyle: React.CSSProperties = {
  width: 'clamp(52px, 8vh, 80px)',
  height: 'clamp(52px, 8vh, 80px)',
  backgroundColor: '#3B888C',
  padding: 'clamp(7px, 1.2vh, 10px)',
  cursor: 'pointer',
  borderRadius: '50%',
}

const emergencyContainerStyle: React.CSSProperties = {
  marginTop: 'clamp(6px, 1.2vh, 10px)',
}

const emergencyTelContainerStyle: React.CSSProperties = {
  backgroundColor: '#28454D',
  borderRadius: 25,
  padding: 'clamp(8px, 1.5vh, 20px)',
  cursor: 'pointer',
  // Fixed height + centered content so all four buttons align even when a label wraps to two
  // lines (e.g. "ΠΟΛΙΤΙΚΗ ΠΡΟΣΤΑΣΙΑ").
  height: 'clamp(78px, 12vh, 120px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}

const emergencyTelLabelStyle: React.CSSProperties = {
  fontFamily: 'Alegreya Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: 'clamp(13px, 1.9vh, 18px)',
  lineHeight: 1.3,
  color: 'white',
  textAlign: 'center'
}

const emergencyIconStyle: React.CSSProperties = {
  width: 'clamp(26px, 4.2vh, 40px)',
  height: 'clamp(26px, 4.2vh, 40px)'
}

const pageStyle: React.CSSProperties = {
  backgroundColor: '#1F3539',
  height: '100%',
  width: '100%',
  // No global border-box reset in this app (rsuite scopes its own) — without this,
  // width:100% + side paddings overflows the viewport horizontally on phones.
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  padding: 'clamp(12px, 3vw, 30px)',
  paddingTop: 'clamp(12px, 2.5vh, 50px)',
  paddingBottom: 'clamp(6px, 1vh, 12px)'
}

const HomeUserPage = () => {
  useUserSubscriptions()
  useUsersSubscriptions()

  const auth = useAuthContext()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user, room, roomMetrics, roomCreated } = useAppSelector(userSelector)

  // Upon room request completion navigate to room
  useEffect(() => {
    if (room) {
      const { id } = room
      const path = `/rooms/${id}`

      navigatePage(dispatch, navigate, path, { state: { room } })
    }
  }, [room, dispatch, navigate])

  const call = (is_emergency: boolean, room_type: RoomType) => {
    if (user && !room) {
      // Room has not been requested
      let room: CreateRoom = { user_id: user.id, is_emergency, invite_users: [], room_type }
      dispatch(postUserRoomCreate(room))
    }
  }

  return (
    <>
      {(roomCreated || room) && <BeforeCallUserMobile/>}
      <div style={pageStyle}>
        <Grid fluid style={{ width: '100%', padding: 0 }}>
          <Row>
            <Col xs={19} sm={19} md={19}>
              <p style={welcomeMessageStyle}>Καλώς ήλθες</p>
              <p style={welcomeMessageNameStyle}>
                <span onClick={() => navigatePage(dispatch, navigate, '/user')}>{user?.token?.first_name}</span>
                <span onClick={() => auth.logout()} style={{ paddingLeft: 10, fontSize: 22 }}>
            <ExitIcon/>
          </span>
              </p>
            </Col>
            <Col xs={5} sm={5} md={5}>
              <Avatar circle src={user?.info.person.image ?? '/img/avatar.svg'} size="lg"
                      style={{ border: '1px solid white', float: 'right', cursor: 'pointer' }}
                      onClick={() => navigatePage(dispatch, navigate, '/user')}/>
            </Col>
          </Row>

          <Row style={{ paddingTop: 'clamp(10px, 2.2vh, 30px)' }}>
            <p style={callUsStyle}>Κάλεσέ μας</p>
            <div style={statsContainerStyle}>
              <FlexboxGrid>
                <FlexboxGrid.Item colspan={7} style={{ textAlign: 'center', paddingTop: 10 }}>
                  <p style={statsNumberStyle}>{roomMetrics.connected_interpreters}</p>
                  <img src="/img/home/interpreter.svg" alt="διερμηνείς" style={statsImageStyle}/>
                  <p style={statsLabelStyle}>διερμηνείς σε σύνδεση</p>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={10} style={{ textAlign: 'center' }}>
                  <img src="/img/home/eik.png" alt="Ίδρυμα κοφών"
                       style={{
                         border: '1px solid white',
                         borderRadius: '50%',
                         backgroundColor: '#F6F4F4',
                         padding: 7,
                         width: 'clamp(76px, 12vh, 128px)',
                         height: 'clamp(76px, 12vh, 128px)'
                       }}/>
                  <div style={{ paddingTop: 'clamp(6px, 1.5vh, 15px)' }}>
                    <img src="/img/icons/call.svg" style={callButtonStyle} alt="Κάλεσε τώρα"
                         onClick={() => call(false, RoomType.SIMPLE)}/>
                  </div>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={7} style={{ textAlign: 'center', paddingTop: 10 }}>
                  <p style={statsNumberStyle}>{roomMetrics.users_waiting}</p>
                  <img src="/img/home/deaf.svg" alt="χρήστες σε αναμονή" style={statsImageStyle}/>
                  <p style={statsLabelStyle}>χρήστες σε αναμονή</p>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </div>
          </Row>

          <Row style={{ paddingTop: 'clamp(8px, 1.8vh, 20px)' }}>
            <p style={callUsStyle}>ΖΗΤΗΣΤΕ ΔΙΕΡΜΗΝΕΑ ΠΡΟΤΕΡΑΙΟΤΗΤΑΣ ΓΙΑ:</p>
            <p style={{ color: '#B7CBCE', fontSize: 12, paddingLeft: 10, paddingRight: 10, marginTop: 0 }}>
              Η εφαρμογή δεν καλεί τις υπηρεσίες έκτακτης ανάγκης — σας συνδέει με διερμηνέα, ο οποίος
              επικοινωνεί με την αρμόδια υπηρεσία για εσάς.
            </p>
            <div style={emergencyContainerStyle}>
              <FlexboxGrid>
                <FlexboxGrid.Item colspan={12} style={{ textAlign: 'center', paddingRight: 5 }}>
                  <div style={emergencyTelContainerStyle}
                       onClick={() => call(true, RoomType.AMBULANCE)}>
                    <img src="/img/icons/ambulance.svg" alt="ΑΣΘΕΝΟΦΟΡΟ" style={emergencyIconStyle}/>
                    <p style={emergencyTelLabelStyle}>ΑΣΘΕΝΟΦΟΡΟ</p>
                  </div>
                  <div style={{ ...emergencyTelContainerStyle, marginTop: 'clamp(6px, 1.2vh, 10px)' }}
                       onClick={() => call(true, RoomType.POLICE)}>
                    <img src="/img/icons/police.svg" alt="ΑΣΤΥΝΟΜΙΑ" style={emergencyIconStyle}/>
                    <p style={emergencyTelLabelStyle}>ΑΣΤΥΝΟΜΙΑ</p>
                  </div>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={12} style={{ textAlign: 'center', paddingLeft: 5 }}>
                  <div style={emergencyTelContainerStyle}
                       onClick={() => call(true, RoomType.CIVIL_PROTECTION)}>
                    <img src="/img/icons/civil_protection.svg" alt="ΠΟΛΙΤΙΚΗ ΠΡΟΣΤΑΣΙΑ" style={emergencyIconStyle}/>
                    <p style={emergencyTelLabelStyle}>ΠΟΛΙΤΙΚΗ ΠΡΟΣΤΑΣΙΑ</p>
                  </div>
                  <div style={{ ...emergencyTelContainerStyle, marginTop: 'clamp(6px, 1.2vh, 10px)' }}
                       onClick={() => call(true, RoomType.FIRE_DEPARTMENT)}>
                    <img src="/img/icons/fire.svg" alt="ΠΥΡΟΣΒΕΣΤΙΚΗ" style={emergencyIconStyle}/>
                    <p style={emergencyTelLabelStyle}>ΠΥΡΟΣΒΕΣΤΙΚΗ</p>
                  </div>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </div>
          </Row>
        </Grid>

        {/* In-flow footer: sits at the bottom of the flex column, never overlaps content */}
        <div style={{ width: '100%', marginTop: 'auto', paddingTop: 'clamp(6px, 1.2vh, 16px)' }}>
          <FlexboxGrid>
            <FlexboxGrid.Item colspan={12} style={{ textAlign: 'center' }}>
              <a style={{ color: 'gray', fontSize: 'clamp(12px, 1.7vh, 14px)' }}
                 href={"https://myiris.gr/%cf%80%ce%bf%ce%bb%ce%b9%cf%84%ce%b9%ce%ba%ce%ae-%ce%b1%cf%80%ce%bf%cf%81%cf%81%ce%ae%cf%84%ce%bf%cf%85-iris/"}
                 target={"_blank"} rel="noreferrer">
                Πολιτική Απορρήτου
              </a>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={12} style={{ textAlign: 'center' }}>
              <a style={{ color: 'gray', fontSize: 'clamp(12px, 1.7vh, 14px)' }}
                 href={"https://myiris.gr/%cf%8c%cf%81%ce%bf%ce%b9-%cf%87%cf%81%ce%ae%cf%83%ce%b7%cf%82/"}
                 target={"_blank"} rel="noreferrer">
                Όροι Χρήσης
              </a>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </div>
      </div>
    </>
  )
}

export default HomeUserPage
